import { useState, useMemo } from 'react';
import { T, useTheme, dens, Eyebrow, Btn, Card, StatusBadge } from '../../../design';
import type { SalonEnrollment } from '../../../types/courses.types';
import { fmtDate } from '../../../utils';
import { PersonRow } from './PersonRow';

export interface SalonOverviewProps {
  enrollments: SalonEnrollment[];
  onBulkCancel: (enrollments: SalonEnrollment[]) => void;
}

export function SalonOverview({ enrollments, onBulkCancel }: SalonOverviewProps) {
  const t = useTheme();
  const d = dens(t);
  const [filter, setFilter] = useState('all');
  const [selectMode, setSelectMode] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const courses = useMemo(() => {
    const m: Record<string, string> = {};
    enrollments.forEach((e) => { m[e.courseSlug] = e.courseProductName; });
    return Object.entries(m);
  }, [enrollments]);
  const list = enrollments.filter((e) => filter === 'all' || e.courseSlug === filter);
  const toggle = (id: number) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap, paddingBottom: selectMode ? 88 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Eyebrow>Salonens tilmeldinger · {enrollments.length}</Eyebrow>
        <button onClick={() => { setSelectMode((s) => !s); setSel(new Set()); }} style={{ border: 0, background: 'transparent', color: selectMode ? T.error : t.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>{selectMode ? 'Annullér' : 'Vælg'}</button>
      </div>
      <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 2 }}>
        {[['all', 'Alle kurser'] as [string, string], ...courses].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} style={{ padding: '7px 13px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0, border: `1.5px solid ${filter === k ? t.accent : T.border}`, background: filter === k ? T.accentLight : T.surface, color: filter === k ? t.accent : T.fg2, fontFamily: T.font }}>{l}</button>)}
      </div>
      <Card pad={0}>
        {list.length === 0 ? <div style={{ padding: '28px', textAlign: 'center', color: T.fg3, fontSize: 13, fontWeight: 600 }}>Ingen tilmeldinger for dette kursus.</div>
          : list.map((e) => {
            const pickable = selectMode && (e.status === 'approved' || e.status === 'pending');
            return <PersonRow key={e.id} name={e.employeeName} sub={`${e.courseProductName} · ${fmtDate(e.courseDate)}`} selectable={pickable} selected={sel.has(e.id)} onClick={pickable ? () => toggle(e.id) : null} right={!selectMode ? <StatusBadge status={e.status} /> : undefined} />;
          })}
      </Card>
      {selectMode && <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, background: T.surface, borderTop: `1px solid ${T.border}`, padding: '12px 14px calc(12px + env(safe-area-inset-bottom))', maxWidth: 440, margin: '0 auto' }}>
        <Btn full size="lg" variant="danger" disabled={sel.size === 0} onClick={() => { onBulkCancel([...sel].map((id) => enrollments.find((e) => e.id === id)!).filter(Boolean)); setSelectMode(false); setSel(new Set()); }}>Afmeld {sel.size > 0 ? sel.size : ''} valgte</Btn>
      </div>}
    </div>
  );
}
